import { useReadOnly } from "../../hooks/useReadOnly";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../../components/common/NavBar";
import LocationSearchBar from "../../components/common/LocationSearchBar";
import SavePolygonModal from "../../components/areas/SavePolygonModal";
import PolygonEditorMap from "../../components/areas/PolygonEditorMap";
import type { EditorMode, PolygonEditorHandle } from "../../components/areas/PolygonEditorMap";
import PolygonDrawToolbar from "../../components/areas/PolygonDrawToolbar";
import { userMapsApi } from "../../services/userMapsApi";
import { projectsApi, ProjectTarget } from "../../services/projectsApi";
import { COUNTRIES, COUNTRY_LIST, DEFAULT_COUNTRY, areaCountry } from "../../config/countries";
import type { CountryCode } from "../../config/countries";
import { useCountryFilter } from "../../hooks/useCountryFilter";
import { countryForGeometry } from "../../utils/countryForGeometry";
import { buildCustomPolygonWrite } from "../../utils/customPolygonWrite";
import { shapeToRing, validateRing } from "../../utils/polygonEditor";
import { currentId, emptyQueue, reduceQueue, type QueueResult, type SaveQueue } from "../../utils/polygonSaveQueue";

interface RouteState {
  editMode?: boolean;
  savedArea?: any;
  wktGeometry?: string;
}

const CustomPolygonDraw: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const handleRef = useRef<PolygonEditorHandle | null>(null);
  // Read-only (support view or a viewer): drawing is fine, saving isn't.
  const { readOnly } = useReadOnly();
  const routeState = location.state as RouteState | null;
  const editMode = !!(routeState?.editMode && routeState?.savedArea && routeState?.wktGeometry);
  const savedArea = editMode ? routeState!.savedArea : null;
  // The country the polygons are saved under. An area being edited keeps its own; a new one
  // starts in the registry's first country.
  const [country, setCountry] = useState<CountryCode>(() => {
    const saved = (routeState as { savedArea?: { country_code?: string | null; type?: string } } | null)?.savedArea;
    return (saved && areaCountry(saved)) || DEFAULT_COUNTRY;
  });
  const { country: filteredCountry } = useCountryFilter();
  const savedHadNoCountry = editMode && savedArea != null && areaCountry(savedArea) === null;

  // The polygon of the area being edited, read once from the route.
  const wkt = editMode ? routeState!.wktGeometry! : null;
  const loaded = useMemo(() => (wkt ? shapeToRing(wkt) : null), [wkt]);
  const initialRings = useMemo(() => (loaded ? [loaded.ring] : undefined), [loaded]);
  const initialView = useRef(COUNTRIES[country].defaultView).current;

  const [mode, setMode] = useState<EditorMode>("select");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [queue, setQueue] = useState<SaveQueue>(emptyQueue);
  // The dialog opened with nothing drawn: it says so instead of listing polygons.
  const [emptyDialog, setEmptyDialog] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // A warning about the loaded area that stays until the user leaves; opening the save dialog keeps it.
  const [loadNote, setLoadNote] = useState<string | null>(null);
  // When a save batch targets a NEW project, create it once on the first save
  // and reuse its id for the remaining polygons.
  const batchProjectIdRef = useRef<string | null>(null);

  // An area saved in another country keeps it; the page may have started on the default one.
  useEffect(() => {
    if (editMode) setCountry(areaCountry(savedArea) ?? DEFAULT_COUNTRY);
  }, [editMode, savedArea]);

  useEffect(() => {
    if (!editMode) return;
    if (!loaded) setProblem("Failed to load polygon for editing");
    else if (loaded.droppedHoles) setLoadNote("This area has holes. The outer outline is loaded; holes are removed when you save.");
  }, [editMode, loaded]);

  const handleReady = useCallback((handle: PolygonEditorHandle) => {
    handleRef.current = handle;
    // A fresh editor starts in select mode: a loaded polygon is editable straight away.
    setMode("select");
    setSelectedId(null);
  }, []);

  const changeMode = useCallback((next: EditorMode) => {
    handleRef.current?.setMode(next);
    setMode(next);
  }, []);

  const deleteSelected = useCallback(() => {
    handleRef.current?.removeSelected();
    setSelectedId(null);
  }, []);

  // Delete removes the selected shape, Esc goes back to select. A key typed into a field is left alone,
  // and so is any key while the save dialog is open.
  const dialogOpen = queue.open || emptyDialog;
  useEffect(() => {
    if (dialogOpen) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        if (handleRef.current?.selectedId()) {
          e.preventDefault();
          deleteSelected();
        }
      } else if (e.key === "Escape") {
        changeMode("select");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [changeMode, deleteSelected, dialogOpen]);

  const handleLocationSearch = (lat: number, lng: number) => {
    handleRef.current?.jumpTo({ latitude: lat, longitude: lng, zoom: 13 });
  };

  // Changing the country recentres an empty map on it; a map with polygons stays where it is.
  const handleCountryChange = (code: CountryCode) => {
    setCountry(code);
    const handle = handleRef.current;
    if (handle && handle.getPolygons().length === 0) handle.jumpTo(COUNTRIES[code].defaultView);
  };

  // Select a polygon and zoom to it, so the dialog's "1 of 3" is the one in view.
  const highlight = (id: string | null) => {
    const handle = handleRef.current;
    if (!handle || !id) return;
    const poly = handle.getPolygons().find((p) => p.id === id);
    if (!poly) return;
    handle.select(id);
    setMode("select");
    handle.fitTo([poly.ring]);
  };

  const closeDialog = () => {
    handleRef.current?.deselect();
    setSelectedId(null);
    setEmptyDialog(false);
    setQueue((q) => reduceQueue(q, { type: "cancel" }).state);
  };

  const openSaveModal = () => {
    const handle = handleRef.current;
    if (!handle) return;
    batchProjectIdRef.current = null;
    setProblem(null);
    const polygons = handle.getPolygons();
    if (polygons.length === 0) {
      setEmptyDialog(true);
      return;
    }

    // A shape that crosses itself cannot be saved: show the first one so it can be fixed.
    for (let i = 0; i < polygons.length; i++) {
      const check = validateRing(polygons[i].ring);
      if (check.ok) continue;
      setProblem(
        check.reason === "self-intersecting"
          ? `Shape ${i + 1} crosses itself; fix it before saving`
          : `Shape ${i + 1} needs at least three points; fix it before saving`
      );
      handle.select(polygons[i].id);
      setMode("select");
      handle.fitTo([polygons[i].ring]);
      return;
    }

    const opened = reduceQueue(queue, { type: "open", ids: polygons.map((p) => p.id) });
    setQueue(opened.state);
    highlight(currentId(opened.state));
  };

  // The country a polygon is saved under: the edited area's own when it has one, otherwise where it is drawn.
  const countryOf = (ring: number[][], keepsSaved: boolean): CountryCode | null =>
    (keepsSaved ? areaCountry(savedArea ?? {}) : null) ?? countryForGeometry(ring);

  const handleSavePolygon = async (
    name: string,
    description: string,
    project?: ProjectTarget,
    chosenCountry?: CountryCode
  ): Promise<void> => {
    const handle = handleRef.current;
    if (!handle) throw new Error("Map not initialized");
    const id = currentId(queue);
    const polygon = handle.getPolygons().find((p) => p.id === id);
    if (!id || !polygon) throw new Error("No polygon found");

    const resolved = countryOf(polygon.ring, editMode && !queue.editApplied && queue.index === 0) ?? chosenCountry;
    if (!resolved) throw new Error("Choose a country");

    const write = buildCustomPolygonWrite({
      ring: polygon.ring,
      name,
      description,
      country: resolved,
      index: queue.editApplied ? 1 : queue.index,
      editAreaId: editMode && !queue.editApplied ? savedArea?.id : undefined,
    });

    if (write.kind === "update") {
      // Update the area being edited
      await userMapsApi.updateGeometries(write.id, write.body);
    } else {
      // Save as a new area
      const result = await userMapsApi.saveGeometries(write.body);

      // Optionally link the freshly-saved area to a project.
      if (project && result.id) {
        try {
          if (project.kind === "existing") {
            await projectsApi.addAreas(project.id, [result.id]);
          } else if (batchProjectIdRef.current) {
            await projectsApi.addAreas(batchProjectIdRef.current, [result.id]);
          } else {
            const created = await projectsApi.create({
              name: project.name,
              areaIds: [result.id],
            });
            batchProjectIdRef.current = created.id;
          }
        } catch (err) {
          console.error("Failed to link polygon to project:", err);
        }
      }
    }

    // Saved: off the map and out of the queue.
    handle.remove(id);
    const next = reduceQueue(queue, { type: "saved", update: write.kind === "update" });
    setQueue(next.state);
    finishStep(next);
  };

  // After a save or a skip: the dialog moves on to the next polygon, or the batch is over.
  // Skipped drawings stay on the map, so the page only leaves once nothing is left drawn.
  const finishStep = (next: QueueResult) => {
    setQueue(next.state);
    if (!next.done) {
      highlight(currentId(next.state));
      return;
    }
    handleRef.current?.deselect();
    setSelectedId(null);
    if ((handleRef.current?.getPolygons().length ?? 0) === 0) navigate("/?section=areas");
  };

  const handleSkipPolygon = () => {
    finishStep(reduceQueue(queue, { type: "skip" }));
  };

  // The save dialog is an edit of the saved area only for its first polygon.
  const editsSavedArea = editMode && !queue.editApplied && queue.index === 0;
  // The shape being saved has no country the page can work out, so the dialog asks.
  const currentRing = queue.open ? handleRef.current?.getPolygons().find((p) => p.id === currentId(queue))?.ring : undefined;
  const askCountry = currentRing !== undefined && countryOf(currentRing, editsSavedArea) === null;

  return (
    <div className="flex flex-col h-screen">
      <NavBar
        title={editMode ? `Edit: ${savedArea?.name || "Custom Polygon"}` : "Draw Custom Polygon"}
        showBackButton={true}
      />
      <div className="relative flex-grow">
        <LocationSearchBar
          onSearch={handleLocationSearch}
          country={country}
          placeholder={`Search an address in ${COUNTRIES[country].label}`}
        />
        <PolygonEditorMap
          initialView={initialView}
          initialRings={initialRings}
          onInitialLoadFailed={() => setProblem("Failed to load polygon for editing")}
          onReady={handleReady}
          onSelectionChange={setSelectedId}
        >
          <PolygonDrawToolbar mode={mode} hasSelection={selectedId !== null} onMode={changeMode} onDelete={deleteSelected} />
        </PolygonEditorMap>
        <div className="absolute top-56 sm:top-20 right-4 z-10 w-56 rounded-box bg-surface p-3 shadow-md">
          <label className="form-control w-full max-w-xs">
            <span className="label-text text-ink-soft">Country</span>
            <select
              className="select select-bordered select-sm"
              value={country}
              onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
            >
              {COUNTRY_LIST.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.label}
                </option>
              ))}
            </select>
          </label>
          {savedHadNoCountry && (
            <p className="mt-2 text-xs text-ink-muted">
              This area was saved without a country. It is set from where it is drawn when you save.
            </p>
          )}
          {!COUNTRIES[country].realtime && (
            <p className="mt-2 text-xs text-ink-muted">
              Real Time isn't available for {COUNTRIES[country].label} yet. You can still save the area.
            </p>
          )}
        </div>
        {loadNote && (
          <div
            role="status"
            className="absolute bottom-28 left-1/2 z-10 max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-box bg-surface px-4 py-2 text-sm text-ink-soft shadow-md"
          >
            {loadNote}
          </div>
        )}
        {problem && (
          <div
            role="alert"
            className="absolute bottom-16 left-1/2 z-10 max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-box bg-surface px-4 py-2 text-sm text-error shadow-md"
          >
            {problem}
          </div>
        )}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          {readOnly ? (
            <span className="rounded-full bg-surface px-4 py-2 text-sm text-ink-muted shadow-card">
              Read-only: you can look, but areas can't be saved here.
            </span>
          ) : (
            <button onClick={openSaveModal} className="btn btn-primary">
              {editMode ? "Update Polygon" : "Save Polygon"}
            </button>
          )}
        </div>
      </div>
      <SavePolygonModal
        isOpen={dialogOpen}
        onClose={closeDialog}
        onSave={handleSavePolygon}
        hasPolygons={queue.open}
        editMode={editsSavedArea}
        initialName={editsSavedArea ? savedArea?.name || "" : ""}
        initialDescription={editsSavedArea ? savedArea?.description || "" : ""}
        onSkip={handleSkipPolygon}
        currentPolygonNumber={queue.index + 1}
        totalPolygons={queue.ids.length}
        askCountry={askCountry}
        defaultCountry={filteredCountry}
      />
    </div>
  );
};

export default CustomPolygonDraw;
